import { Link } from 'react-router-dom'
import Icon from './Icon'

/**
 * One button, three possible elements.
 *
 * `to` renders a router <Link>, `href` an <a> (external links get
 * rel="noopener noreferrer"), and neither renders a <button>. Keeping that
 * choice in one place is what stops "buttons" that are really divs from
 * creeping in.
 *
 * @param {object} props
 * @param {'primary'|'secondary'|'outline'|'ghost-dark'} [props.variant]
 * @param {'sm'|'md'|'lg'} [props.size]
 * @param {string} [props.icon]        Icon name appended after the label.
 * @param {string} [props.iconBefore]  Icon name placed before the label.
 * @param {boolean} [props.block]      Full width.
 */
export default function Button({
  children,
  to,
  href,
  variant = 'primary',
  size = 'md',
  icon,
  iconBefore,
  block = false,
  className = '',
  type = 'button',
  ...rest
}) {
  const classes = [
    'btn',
    `btn--${variant}`,
    size !== 'md' ? `btn--${size}` : '',
    block ? 'btn--block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const content = (
    <>
      {iconBefore ? <Icon name={iconBefore} className="btn__icon" /> : null}
      <span>{children}</span>
      {icon ? (
        <Icon
          name={icon}
          className={`btn__icon${icon === 'arrowRight' ? ' btn__icon--arrow' : ''}`}
        />
      ) : null}
    </>
  )

  if (to) {
    return (
      <Link to={to} className={classes} {...rest}>
        {content}
      </Link>
    )
  }

  if (href) {
    const external = /^https?:\/\//i.test(href)
    return (
      <a
        href={href}
        className={classes}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        {...rest}
      >
        {content}
      </a>
    )
  }

  return (
    <button type={type} className={classes} {...rest}>
      {content}
    </button>
  )
}
